import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { HashLoader } from "react-spinners";
import { formatAsMoney } from "../../helpers/numbers";
import {
  CaretRight,
  ShoppingBag,
  CirclesThreePlus,
  MapPinLine,
  CurrencyNgn,
  CirclesThree,
} from "@phosphor-icons/react"; // choose icons you use
import useOrders from "../../hooks/useOrders";
import { getOrderStatusMeta } from "../../helpers/colors";

const OrdersList = () => {
  const navigate = useNavigate();
  const { orders, isLoading, refreshOrders, isRefetching } = useOrders();

  useEffect(() => {
    // If you need to auto-fetch or refresh on mount
    if (!orders) {
      refreshOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* loader overlay */}
      {(isLoading || isRefetching) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {/* Orders List */}
      <div className="flex flex-col gap-4 px-5 md:px-10 py-5">
        {orders?.length ? (
          orders.map((item: any) => {
            const statusMeta = getOrderStatusMeta(item.status);

            const totalAmount = item.items.reduce(
              (sum: number, it: any) => sum + parseFloat(it.subtotal || "0"),
              0
            );

            return (
              <div
                key={item.id}
                onClick={() =>
                  navigate(`/payments/orders/${item.id}`, {
                    state: { order: item },
                  })
                }
                className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-4 hover:shadow"
              >
                {/* Top */}
                <div className="flex items-center justify-between gap-3">
                  <div className="p-2 bg-visaro-orange/10 rounded-full">
                    <ShoppingBag
                      size={20}
                      weight="bold"
                      className="text-visaro-orange"
                    />
                  </div>
                  <div className="flex-1 ml-3">
                    <div className="font-semibold">
                      Order #{item.id.slice(0, 8)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {moment(item.created_at).format("MMM D, YYYY h:mm A")}
                    </div>
                  </div>
                  <CaretRight
                    size={20}
                    className="text-visaro-orange"
                    weight="bold"
                  />
                </div>

                {/* Bottom */}
                <div className="border-t border-gray-200 pt-3 flex flex-col gap-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <CirclesThreePlus size={16} />
                      <span className="text-gray-500">Items</span>
                    </div>
                    <span className="font-medium text-right">
                      {item.items[0]?.product?.name}
                      {item.items.length > 1 ? " + more" : ""}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <MapPinLine size={16} />
                      <span className="text-gray-500">Shipping Address</span>
                    </div>
                    <span className="font-medium text-right truncate max-w-[200px]">
                      {item.shipping_address}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <CurrencyNgn size={16} />
                      <span className="text-gray-500">Total Amount</span>
                    </div>
                    <span className="font-medium">
                      ₦ {formatAsMoney(totalAmount)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <CirclesThree size={16} />
                      <span className="text-gray-500">Status</span>
                    </div>
                    <span
                      className={`text-xs px-3 py-1 rounded-md font-medium capitalize bg-${statusMeta.color}-100 border border-${statusMeta.color}-300 text-${statusMeta.color}-600`}
                    >
                      {statusMeta.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-gray-500 mt-5">No orders found</div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;
